import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { toUserMessage } from '@platform/api-client';

import { PATHS } from '@/shared/constants/app/routes.constant.ts';

import {
  useCreatePumsemGroup,
  usePumsemDivisions,
  usePumsemEditionDivisions,
  usePumsemManageEditions,
  usePumsemManageTree,
  useRemovePumsemGroup,
  useRemovePumsemItem,
  useReorderPumsemGroups,
  useUpdatePumsemGroup,
} from '../../api/use-pumsem-manage.query';
import { PumsemChangeBadge } from '../../components/PumsemChangeBadge';
import { PumsemDivisionPanel } from '../../components/manage/PumsemDivisionPanel';
import { PumsemEditionPanel } from '../../components/manage/PumsemEditionPanel';
import { PumsemImportPanel } from '../../components/manage/PumsemImportPanel';
import {
  BTN_GHOST,
  BTN_PRIMARY,
  FIELD,
} from '../../components/manage/pumsemFormStyles';
import { PUMSEM_ERROR, codeOf } from '../../lib/pumsemError';
import { EDITION_PARAM, pumsemItemPath } from '../../lib/pumsemNavigation';
import type { PumsemTreeNode } from '../../model/pumsem.type';

/**
 * 품셈해설 관리 첫 화면 — 판 / 부문 / 장·절 / 항목.
 *
 * 위에서 아래로 **범위가 좁아진다.** 판을 고르지 않으면 아래가 무엇에 매인
 * 것인지 알 수 없고, 부문이 없으면 장·절을 걸 곳이 없다. 그래서 순서가 곧
 * 의존 관계다.
 *
 * 판을 안 고르면 최신 공개판이 잡히는데, **숨긴 판을 만들어 놓고 그걸 고르는
 * 것이 정상 흐름**이다(공개판을 직접 고치는 건 오탈자 정정 같은 예외다).
 */
export function PumsemManageView() {
  const [edition, setEdition] = useState<string | null>(null);
  const editionsQuery = usePumsemManageEditions();
  const treeQuery = usePumsemManageTree(edition);
  const divisionsQuery = usePumsemEditionDivisions(edition);
  const allQuery = usePumsemDivisions();

  const editions = editionsQuery.data;
  const tree = treeQuery.data;
  const editionDivisions = divisionsQuery.data;
  const allDivisions = allQuery.data;
  const isPending = treeQuery.isPending;

  // 판이 **하나도 없는** 상태는 장애가 아니라 첫 화면이다. 서버가 트리를
  // 404 로 답하는데 그걸 실패로 묶으면, 갓 설치한 관리자에게 "로그인이 풀렸을
  // 수 있습니다" 라고 말하게 된다.
  const noEdition =
    treeQuery.isError &&
    codeOf(treeQuery.error) === PUMSEM_ERROR.NO_PUBLISHED_EDITION;

  // 나머지는 진짜 실패다. `?? []` 로 삼키면 403(세션 만료)·500 이 "데이터가
  // 없다" 로 보인다 — 부문 패널은 "이 판 34건" 이라고 말하는데 트리는 비어
  // 있는 화면이 그렇게 만들어지고, 관리자는 데이터가 지워진 줄 안다.
  const loadFailed =
    editionsQuery.isError ||
    allQuery.isError ||
    (treeQuery.isError && !noEdition);
  const retryAll = () => {
    void editionsQuery.refetch();
    void treeQuery.refetch();
    void divisionsQuery.refetch();
    void allQuery.refetch();
  };

  const activeEdition = edition ?? tree?.edition.slug ?? '';

  // 부문 펼침 상태. 부문이 다섯이면 장·절이 수백 줄이라 한 화면에 다 펴면
  // 원하는 부문까지 스크롤로만 내려가야 한다. 판이 바뀌면 자리도 달라지므로
  // 판별로 기억한다.
  const [openDivisions, setOpenDivisions] = useState<Set<string>>(
    () => new Set(),
  );
  const [openedFor, setOpenedFor] = useState<string | null>(null);

  // 부문이 하나뿐이면 접어 둘 이유가 없다. 판을 바꾸면 다시 판단한다.
  if (tree && openedFor !== activeEdition) {
    setOpenedFor(activeEdition);
    setOpenDivisions(
      new Set(
        tree.divisions.length === 1
          ? tree.divisions.map((division) => division.slug)
          : [],
      ),
    );
  }

  const toggleDivision = (slug: string) => {
    setOpenDivisions((prev) => {
      const next = new Set(prev);
      if (!next.delete(slug)) next.add(slug);
      return next;
    });
  };
  // 아직 모르는 상태(`undefined`)를 "공개 중" 과 구분한다. 둘을 합치면 목록이
  // 도착하기 전에는 늘 "공개 중인 판에는 임포트할 수 없습니다" 가 떠서, 숨긴
  // 판을 고르고 들어온 관리자에게 첫 화면이 틀린 이유를 말한다.
  const currentEdition = editions?.find((item) => item.slug === activeEdition);
  const isHidden = currentEdition
    ? currentEdition.isPublished === false
    : undefined;

  const attached = new Set((editionDivisions ?? []).map((d) => d.slug));
  const available = (allDivisions ?? []).filter((d) => !attached.has(d.slug));
  const itemCount = (editionDivisions ?? []).reduce(
    (sum, division) => sum + division.itemCount,
    0,
  );

  return (
    <div className="mx-auto w-full max-w-[80rem] px-4 py-10 sm:px-8">
      <h1 className="mb-6 text-[24px] font-black tracking-tight text-neutral-900 dark:text-neutral-50">
        품셈해설 관리
      </h1>

      {loadFailed && (
        <p
          className="mb-6 rounded-xl border border-danger/40 bg-danger/5 px-4 py-3 text-[13px] text-danger"
          role="alert"
        >
          관리 목록을 불러오지 못했습니다. 로그인이 풀렸을 수 있습니다.{' '}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={retryAll}
          >
            다시 시도
          </button>
        </p>
      )}

      <PumsemEditionPanel
        editions={editions ?? []}
        selected={edition}
        onSelect={setEdition}
        active={activeEdition}
        itemCount={itemCount}
      />

      {activeEdition && (
        <ManageHints edition={activeEdition} isHidden={isHidden} />
      )}

      {/*
        판이 정해지기 전에는 아래 패널을 그리지 않는다. 작은 응답인 부문 목록이
        트리보다 먼저 도착하면 `activeEdition` 이 아직 빈 문자열인데 칩이 눌리는
        상태로 떠 있고, 누르면 `/editions//divisions` 로 나간다. 부문 만들기의
        `attachTo` 도 조용히 "어디에도 안 싣기" 가 된다.
      */}
      {activeEdition ? (
        <>
          <PumsemDivisionPanel
            divisions={editionDivisions ?? []}
            available={available}
            edition={activeEdition}
          />

          <PumsemImportPanel
            edition={activeEdition}
            isHidden={isHidden}
            attached={[...attached]}
          />
        </>
      ) : (
        !loadFailed && (
          <p className="mb-8 rounded-xl border border-dashed border-neutral-200 px-4 py-6 text-center text-[13px] text-neutral-400 dark:border-white/12">
            {noEdition ? (
              <>
                등록된 개정판이 없습니다. 위 <b>판 추가</b> 로 첫 판을 만드세요 —
                판이 있어야 부문·장·절과 항목을 걸 수 있습니다.
              </>
            ) : (
              '개정판을 먼저 고르세요. 판이 정해져야 부문·장·절을 걸 수 있습니다.'
            )}
          </p>
        )
      )}

      {isPending && !treeQuery.isError && (
        <p className="py-16 text-center text-[14px] text-neutral-500">
          불러오는 중…
        </p>
      )}

      {tree && tree.divisions.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[12.5px] text-neutral-500">
            부문 {tree.divisions.length}개
          </span>
          <button
            type="button"
            className="text-[12.5px] text-brand-700 hover:underline dark:text-brand-300"
            onClick={() => setOpenDivisions(new Set())}
          >
            모두 접기
          </button>
          <button
            type="button"
            className="text-[12.5px] text-brand-700 hover:underline dark:text-brand-300"
            onClick={() =>
              setOpenDivisions(new Set(tree.divisions.map((d) => d.slug)))
            }
          >
            모두 펼치기
          </button>
        </div>
      )}

      {tree?.divisions.map((division) => (
        <DivisionSection
          key={division.slug}
          edition={activeEdition}
          divisionSlug={division.slug}
          title={division.title}
          nodes={division.nodes}
          canReorder={isHidden === true}
          open={openDivisions.has(division.slug)}
          onToggle={() => toggleDivision(division.slug)}
        />
      ))}
    </div>
  );
}

/**
 * 이 화면에서 **안 되는 것**과 그럼 어디로 가야 하는지.
 *
 * 관리 트리는 제목만 보여준다 — 본문과 품 표는 안 그린다(수천 항목의 표를
 * 트리에 실으면 판 하나 여는 데 수 MB 다). 그래서 "이 항목 내용이 맞나" 를
 * 확인하려면 뷰어로 가야 하는데, 그 사실이 화면 어디에도 없어서 관리자는
 * 관리 화면만 붙들고 있었다. 그리고 숨긴 판에서만 되는 동작들은 공개판을
 * 고른 순간 **그냥 사라진다** — 없어진 건지 원래 없는 건지 알 수 없다.
 */
function ManageHints({
  edition,
  isHidden,
}: {
  edition: string;
  isHidden: boolean | undefined;
}) {
  const viewer = `${PATHS.PUMSEM}?${EDITION_PARAM}=${edition}`;

  return (
    <section className="mb-8 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 dark:border-white/12 dark:bg-white/[0.02]">
      <h2 className="mb-1.5 text-[12.5px] font-bold text-neutral-500">
        이 화면에서 안 되는 것
      </h2>
      <ul className="space-y-1 text-[12.5px] leading-relaxed text-neutral-600 dark:text-neutral-300">
        <li>
          <b>본문·품 표 확인</b> — 여기는 제목만 그립니다. 내용은{' '}
          <Link
            to={viewer}
            className="font-bold text-brand-700 hover:underline dark:text-brand-300"
          >
            뷰어에서 이 판 열기
          </Link>{' '}
          로 보고, 항목 줄의 <b>보기</b> 를 누르면 그 항목이 바로 열립니다.
          거기서도 관리자에게는 <b>수정</b>·<b>삭제</b> 가 함께 보입니다.
        </li>
        {isHidden ? (
          <li>
            <b>지금은 숨긴 판</b> 이라 장·절 순서 변경과 일괄 임포트가 열려
            있습니다. 작업이 끝나면 위에서 <b>공개하기</b> 를 누르세요.
          </li>
        ) : (
          <li>
            <b>장·절 순서 변경 · 일괄 임포트</b> — 공개 중인 판에는 없습니다.
            둘 다 코드를 다시 매기거나 부문을 통째로 갈아끼워서, 남의 견적서가
            인용한 근거가 조용히 다른 값이 됩니다. 필요하면 위에서{' '}
            <b>숨기기</b> 를 먼저 누르세요.
          </li>
        )}
        <li>
          <b>부문 삭제</b> — 부문 행은 모든 판이 공유하고 딥링크의 첫 조각이라
          여기서 지울 수 없습니다. 이번 판에만 빼려면 부문 패널의{' '}
          <b>이 판에서 빼기</b> 를 쓰세요.
        </li>
      </ul>
    </section>
  );
}

interface DivisionSectionProps {
  edition: string;
  divisionSlug: string;
  title: string;
  nodes: PumsemTreeNode[];
  /** 재정렬은 코드를 다시 매긴다 — 숨긴 판에서만 열어 둔다. */
  canReorder: boolean;
  open: boolean;
  onToggle: () => void;
}

/** 이 부문 아래 항목 수. 접힌 헤더에서 규모를 보여주려면 필요하다. */
function countItems(nodes: PumsemTreeNode[]): number {
  return nodes.reduce(
    (sum, node) =>
      sum + (node.isItem ? 1 : countItems(node.children)),
    0,
  );
}

function DivisionSection({
  edition,
  divisionSlug,
  title,
  nodes,
  canReorder,
  open,
  onToggle,
}: DivisionSectionProps) {
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  // 인라인 편집을 닫으면 `autoFocus` 입력이 사라지면서 포커스가 <body> 로
  // 떨어진다 — 키보드 사용자는 `+ 장 추가` 한 번마다 페이지 맨 위로 돌아간다.
  const addRef = useRef<HTMLButtonElement>(null);

  const createGroup = useCreatePumsemGroup(edition);
  const onError = (cause: unknown) => setError(toUserMessage(cause));

  return (
    <section className="mb-4 rounded-xl border border-neutral-200 dark:border-white/12">
      <header className="flex flex-wrap items-center gap-2 px-3 py-2.5">
        <button
          type="button"
          aria-expanded={open}
          className="flex items-center gap-2 text-left"
          onClick={onToggle}
        >
          <span aria-hidden className="w-3 text-[11px] text-neutral-400">
            {open ? '▾' : '▸'}
          </span>
          <h2 className="text-[16px] font-bold text-neutral-900 dark:text-neutral-100">
            {title}
          </h2>
          <span className="text-[12px] text-neutral-400">
            장 {nodes.length} · 항목 {countItems(nodes)}
          </span>
        </button>
        {open && adding ? (
          <>
            <input
              className={`${FIELD} w-64`}
              value={draftTitle}
              autoFocus
              placeholder="장 제목 (예: 적용기준)"
              aria-label="장 제목"
              onChange={(event) => setDraftTitle(event.target.value)}
            />
            <button
              type="button"
              className={BTN_PRIMARY}
              disabled={!draftTitle.trim() || createGroup.isPending}
              onClick={() =>
                createGroup.mutate(
                  { division: divisionSlug, title: draftTitle.trim() },
                  {
                    onSuccess: () => {
                      setAdding(false);
                      setDraftTitle('');
                      addRef.current?.focus();
                    },
                    onError,
                  },
                )
              }
            >
              장 만들기
            </button>
            <button
              type="button"
              className={BTN_GHOST}
              onClick={() => {
                setAdding(false);
                addRef.current?.focus();
              }}
            >
              취소
            </button>
          </>
        ) : (
          open && (
            <button
              type="button"
              ref={addRef}
              className={BTN_GHOST}
              onClick={() => {
                setError(null);
                setAdding(true);
              }}
            >
              + 장 추가
            </button>
          )
        )}
      </header>

      {!open ? null : nodes.length === 0 ? (
        <p className="mx-3 mb-3 rounded-xl border border-dashed border-neutral-200 px-4 py-6 text-center text-[13px] text-neutral-400 dark:border-white/12">
          이 판에 등록된 장·절이 없습니다. <b>+ 장 추가</b> 로 시작하세요 —
          장·절 번호(1, 2, 3 …)는 등록 순서대로 자동 부여됩니다.
        </p>
      ) : (
        <ul className="space-y-1 border-t border-neutral-100 px-1 py-2 dark:border-white/8">
          {nodes.map((node, index) => (
            <GroupRow
              // 재정렬은 **코드를 다시 매긴다** — key 가 코드뿐이면 React 가
              // 같은 인스턴스를 재사용해서, 3장을 펼쳐 둔 채 2장을 내리면 새로
              // 3번이 된 다른 장이 펼쳐진 채 나타난다. 이름 편집 중이면 더
              // 나쁘다: 다른 장의 제목 위에 편집 상자가 열려 있게 된다.
              key={`${node.code}:${node.title}`}
              node={node}
              index={index}
              siblings={nodes}
              parentCode={null}
              edition={edition}
              divisionSlug={divisionSlug}
              depth={0}
              canReorder={canReorder}
            />
          ))}
        </ul>
      )}

      {error && (
        <p className="mt-2 text-[13px] text-danger" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/** 트리 들여쓰기. 인라인 style 인 이유는 Tailwind 가 동적 클래스를 못 스캔해서다. */
const INDENT_PX = 18;
const BASE_PAD_PX = 8;
/** 장·절 줄의 접기 화살표 자리. 항목 줄은 화살표가 없어 그만큼 더 들여쓴다. */
const TOGGLE_GUTTER_PX = 20;

interface GroupRowProps {
  node: PumsemTreeNode;
  index: number;
  /** 같은 부모 아래 형제 전체. 재정렬이 통째로 보내야 하므로 필요하다. */
  siblings: PumsemTreeNode[];
  parentCode: string | null;
  edition: string;
  divisionSlug: string;
  depth: number;
  canReorder: boolean;
}

/**
 * 장·절 한 줄.
 *
 * ── 항목은 기본으로 접는다 ──────────────────────────────────
 * 부문 하나에 항목이 수천 개다. 전부 펼쳐 그리면 첫 렌더에서 화면이 죽는다.
 * 절을 펼칠 때만 그 아래 항목을 그린다 — 응답에는 이미 들어 있으므로
 * 추가 요청은 없고, DOM 만 아낀다.
 */
function GroupRow({
  node,
  index,
  siblings,
  parentCode,
  edition,
  divisionSlug,
  depth,
  canReorder,
}: GroupRowProps) {
  const [open, setOpen] = useState(depth === 0);
  const [renaming, setRenaming] = useState(false);
  const [addingChild, setAddingChild] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const createGroup = useCreatePumsemGroup(edition);
  const updateGroup = useUpdatePumsemGroup(edition);
  const removeGroup = useRemovePumsemGroup(edition);
  const reorderGroups = useReorderPumsemGroups(edition);

  const onError = (cause: unknown) => setError(toUserMessage(cause));

  const groups = node.children.filter((child) => !child.isItem);
  const items = node.children.filter((child) => child.isItem);
  /** 절(하위 장·절이 없는 마디)에만 항목을 매단다. */
  const isSection = groups.length === 0;

  const move = (delta: number) => {
    const to = index + delta;
    if (to < 0 || to >= siblings.length) return;
    const codes = siblings.map((sibling) => sibling.code);
    const [picked] = codes.splice(index, 1);
    codes.splice(to, 0, picked);
    reorderGroups.mutate(
      { division: divisionSlug, parentCode: parentCode ?? undefined, codes },
      { onError },
    );
  };

  return (
    <li>
      <div
        className="flex flex-wrap items-center gap-2 rounded-lg py-1.5 hover:bg-neutral-50 dark:hover:bg-white/5"
        style={{ paddingLeft: BASE_PAD_PX + depth * INDENT_PX }}
      >
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${node.code} ${node.title}`}
          className="w-4 shrink-0 text-[11px] text-neutral-400"
          onClick={() => setOpen((prev) => !prev)}
        >
          {open ? '▾' : '▸'}
        </button>

        <code className="w-16 shrink-0 text-[12px] tabular-nums text-neutral-400">
          {node.code}
        </code>

        {renaming ? (
          <>
            <input
              className={`${FIELD} w-72`}
              value={draftTitle}
              autoFocus
              aria-label="장·절 제목"
              onChange={(event) => setDraftTitle(event.target.value)}
            />
            <button
              type="button"
              className={BTN_PRIMARY}
              disabled={!draftTitle.trim() || updateGroup.isPending}
              onClick={() =>
                updateGroup.mutate(
                  {
                    division: divisionSlug,
                    code: node.code,
                    title: draftTitle.trim(),
                  },
                  { onSuccess: () => setRenaming(false), onError },
                )
              }
            >
              저장
            </button>
            <button
              type="button"
              className={BTN_GHOST}
              onClick={() => setRenaming(false)}
            >
              취소
            </button>
          </>
        ) : (
          <>
            <span className="text-[13.5px] text-neutral-800 dark:text-neutral-100">
              {node.title}
            </span>
            <span className="text-[12px] text-neutral-400">
              {isSection ? `항목 ${items.length}` : `절 ${groups.length}`}
            </span>

            <span className="ml-auto flex items-center gap-2">
              {canReorder && (
                <span className="flex gap-1 text-[12px] text-neutral-400">
                  <button
                    type="button"
                    aria-label={`${node.title} 위로`}
                    disabled={index === 0 || reorderGroups.isPending}
                    className="hover:text-neutral-700 disabled:opacity-30"
                    onClick={() => move(-1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label={`${node.title} 아래로`}
                    disabled={
                      index === siblings.length - 1 || reorderGroups.isPending
                    }
                    className="hover:text-neutral-700 disabled:opacity-30"
                    onClick={() => move(1)}
                  >
                    ↓
                  </button>
                </span>
              )}

              <button
                type="button"
                className="text-[12.5px] font-bold text-brand-700 hover:underline dark:text-brand-300"
                onClick={() => {
                  setError(null);
                  setDraftTitle(node.title);
                  setRenaming(true);
                }}
              >
                이름
              </button>

              {/* 절이면 항목을, 장이면 절을 만든다. 코드는 서버가 매긴다. */}
              {isSection ? (
                <Link
                  to={`${PATHS.PUMSEM_MANAGE_NEW}?edition=${edition}&division=${divisionSlug}&group=${node.code}`}
                  className={BTN_PRIMARY}
                >
                  + 항목 추가
                </Link>
              ) : (
                <button
                  type="button"
                  className={BTN_GHOST}
                  onClick={() => {
                    setError(null);
                    setAddingChild(true);
                    setOpen(true);
                  }}
                >
                  + 절 추가
                </button>
              )}

              {/* 삭제는 `이름`·`+ 절 추가` 와 같은 줄·같은 크기로 붙어 있어
                  오클릭이 쉽다. 판 삭제와 같은 2단계로 맞춘다 — 규모는 이미
                  옆에 `항목 12` 로 떠 있으니 문구에 그대로 쓴다. */}
              {confirmingDelete ? (
                <>
                  <button
                    type="button"
                    className="text-[12.5px] font-bold text-danger hover:underline"
                    disabled={removeGroup.isPending}
                    onClick={() =>
                      removeGroup.mutate(
                        { division: divisionSlug, code: node.code },
                        {
                          onSuccess: () => setConfirmingDelete(false),
                          onError,
                        },
                      )
                    }
                  >
                    {removeGroup.isPending ? '삭제 중…' : '정말 삭제'}
                  </button>
                  <button
                    type="button"
                    className="text-[12.5px] text-neutral-400 hover:text-neutral-700"
                    onClick={() => setConfirmingDelete(false)}
                  >
                    취소
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="text-[12.5px] text-neutral-400 hover:text-danger"
                  onClick={() => {
                    setError(null);
                    setConfirmingDelete(true);
                  }}
                >
                  삭제
                </button>
              )}
            </span>
          </>
        )}
      </div>

      {/* 절이 하나도 없는 장에서도 절을 만들 수 있어야 한다 — 위 버튼은
          `isSection` 이면 '항목 추가'로 바뀌므로, 여기에 따로 둔다. */}
      {open && isSection && items.length === 0 && !addingChild && (
        <div
          className="py-1"
          style={{ paddingLeft: BASE_PAD_PX + (depth + 1) * INDENT_PX }}
        >
          <button
            type="button"
            className="text-[12px] text-neutral-400 hover:text-neutral-700"
            onClick={() => {
              setError(null);
              setAddingChild(true);
            }}
          >
            + 이 아래에 절 만들기
          </button>
        </div>
      )}

      {addingChild && (
        <div
          className="flex flex-wrap items-center gap-2 py-1.5"
          style={{ paddingLeft: BASE_PAD_PX + (depth + 1) * INDENT_PX }}
        >
          <input
            className={`${FIELD} w-64`}
            value={draftTitle}
            autoFocus
            placeholder="절 제목 (예: 일반사항)"
            aria-label="절 제목"
            onChange={(event) => setDraftTitle(event.target.value)}
          />
          <button
            type="button"
            className={BTN_PRIMARY}
            disabled={!draftTitle.trim() || createGroup.isPending}
            onClick={() =>
              createGroup.mutate(
                {
                  division: divisionSlug,
                  parentCode: node.code,
                  title: draftTitle.trim(),
                },
                {
                  onSuccess: () => {
                    setAddingChild(false);
                    setDraftTitle('');
                  },
                  onError,
                },
              )
            }
          >
            절 만들기
          </button>
          <button
            type="button"
            className={BTN_GHOST}
            onClick={() => {
              setAddingChild(false);
              setDraftTitle('');
            }}
          >
            취소
          </button>
        </div>
      )}

      {confirmingDelete && (
        <p
          className="py-1 text-[12.5px] text-danger"
          style={{ paddingLeft: BASE_PAD_PX + (depth + 1) * INDENT_PX }}
        >
          {node.code} {node.title}
          {isSection
            ? ` 과(와) 그 아래 항목 ${items.length}건이`
            : ` 과(와) 그 아래 절 ${groups.length}개가`}{' '}
          이 판에서 빠집니다.
        </p>
      )}

      {error && (
        <p
          className="py-1 text-[12.5px] text-danger"
          style={{ paddingLeft: BASE_PAD_PX + (depth + 1) * INDENT_PX }}
          role="alert"
        >
          {error}
        </p>
      )}

      {open && (
        <>
          {groups.length > 0 && (
            <ul>
              {groups.map((child, childIndex) => (
                <GroupRow
                  key={`${child.code}:${child.title}`}
                  node={child}
                  index={childIndex}
                  siblings={groups}
                  parentCode={node.code}
                  edition={edition}
                  divisionSlug={divisionSlug}
                  depth={depth + 1}
                  canReorder={canReorder}
                />
              ))}
            </ul>
          )}

          {items.length > 0 && (
            <ul>
              {items.map((item) => (
                <ItemRow
                  key={item.code}
                  node={item}
                  depth={depth + 1}
                  edition={edition}
                  divisionSlug={divisionSlug}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </li>
  );
}

/**
 * 항목 한 줄 — 열기 / 수정 / 삭제.
 *
 * 예전에는 제목이 곧 링크였고 그게 전부였다. 밑줄도 색도 없어 **링크로 안
 * 보였고**, 삭제는 그 링크를 눌러 폼까지 들어가야 `이 판에서 제외` 라는 다른
 * 이름으로 있었다. 장·절 줄에는 `이름`·`삭제` 가 나란히 있는데 항목 줄만
 * 비어 있어서, 관리자 입장에서는 "항목은 고치거나 지울 수 없는 것" 으로
 * 읽혔다. 같은 자리에 같은 모양으로 맞춘다.
 *
 * 삭제는 **이 판에서만** 뺀다 — 항목 자체와 다른 판의 내용, 딥링크는 남는다.
 * 그 사실을 확인 문구에 적는다. 장·절 삭제와 같은 2단계다.
 */
function ItemRow({
  node,
  depth,
  edition,
  divisionSlug,
}: {
  node: PumsemTreeNode;
  depth: number;
  edition: string;
  divisionSlug: string;
}) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const removeItem = useRemovePumsemItem();
  // 콜백 안에서는 `node.revisionId` 가 다시 넓어진다 — 여기서 한 번 좁힌다.
  const revisionId = node.revisionId;

  return (
    <li
      className="rounded-lg py-1 hover:bg-neutral-50 dark:hover:bg-white/5"
      style={{
        paddingLeft: BASE_PAD_PX + depth * INDENT_PX + TOGGLE_GUTTER_PX,
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <code className="w-20 shrink-0 text-[12px] tabular-nums text-neutral-400">
          {node.code}
        </code>

        {node.revisionId ? (
          <Link
            to={PATHS.pumsemManageEdit(node.revisionId)}
            className="text-[13.5px] text-neutral-800 underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-600 dark:text-neutral-100 dark:decoration-white/25"
          >
            {node.title}
          </Link>
        ) : (
          <span className="text-[13.5px] text-neutral-800 dark:text-neutral-100">
            {node.title}
          </span>
        )}

        {node.changeKind && <PumsemChangeBadge change={node.changeKind} />}

        <span className="ml-auto flex items-center gap-2">
          {/* 관리 트리는 본문을 안 그린다. 내용을 확인하려면 뷰어로 가야 하고,
              그 한 걸음이 없어서 관리자는 코드만 보고 짐작해야 했다. */}
          <Link
            to={pumsemItemPath(divisionSlug, node.code, edition)}
            className="text-[12.5px] text-neutral-400 hover:text-neutral-700 hover:underline dark:hover:text-neutral-200"
          >
            보기
          </Link>
          {revisionId && (
            <>
              <Link
                to={PATHS.pumsemManageEdit(revisionId)}
                className="text-[12.5px] font-bold text-brand-700 hover:underline dark:text-brand-300"
              >
                수정
              </Link>
              {confirming ? (
                <>
                  <button
                    type="button"
                    className="text-[12.5px] font-bold text-danger hover:underline"
                    disabled={removeItem.isPending}
                    onClick={() =>
                      removeItem.mutate(revisionId, {
                        onSuccess: () => setConfirming(false),
                        onError: (cause) => {
                          setConfirming(false);
                          setError(toUserMessage(cause));
                        },
                      })
                    }
                  >
                    {removeItem.isPending ? '빼는 중…' : '정말 삭제'}
                  </button>
                  <button
                    type="button"
                    className="text-[12.5px] text-neutral-400 hover:text-neutral-700"
                    onClick={() => setConfirming(false)}
                  >
                    취소
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="text-[12.5px] text-neutral-400 hover:text-danger"
                  onClick={() => {
                    setError(null);
                    setConfirming(true);
                  }}
                >
                  삭제
                </button>
              )}
            </>
          )}
        </span>
      </div>

      {confirming && (
        <p className="pt-0.5 text-[12px] text-danger">
          {node.code} {node.title} 이(가) <b>{edition}</b> 판에서 빠집니다.
          항목 자체와 다른 판의 내용, 딥링크는 남습니다.
        </p>
      )}

      {error && (
        <p className="pt-0.5 text-[12px] text-danger" role="alert">
          {error}
        </p>
      )}
    </li>
  );
}
